// src/context/UtilsContext.js
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { get } from "../service/ApiService";
import { axiosAuthInstance } from "../configs/AxiosInstance";
import { getCommonUtils as getCorporateCommonUtils } from "../service/CorporateWellDhanService";
import { fetchCommonDietUtils } from "../features/diet/core/api/dietApi";
import { fetchWorkoutCommonUtils } from "../features/workouts/core/api/workoutApi";

// 1. Create Context
const UtilsContext = createContext();

function safeParseSessionJson(key) {
  try {
    const stored = sessionStorage.getItem(key);
    return stored ? JSON.parse(stored) : null;
  } catch {
    sessionStorage.removeItem(key);
    return null;
  }
}

/** Diet common utils shape evolved; invalidate stale session caches missing newer catalog arrays. */
function isCompleteDietCommonUtils(u) {
  if (!u || typeof u !== "object") return false;
  if (!Array.isArray(u.serving_sizes)) return false;
  if (!Array.isArray(u.nutrients)) return false;
  if (!Array.isArray(u.measuring_units)) return false;
  return true;
}

// 2. Provider
export function UtilsProvider({ children }) {
  // Auth/session/profile utils (always fetched on mount)
  const [sessionUtils, setSessionUtils] = useState(() =>
    safeParseSessionJson("sessionUtils")
  );
  const [profileUtils, setProfileUtils] = useState(() =>
    safeParseSessionJson("profileUtils")
  );
  const [utilsLoading, setUtilsLoading] = useState(true);
  const [utilsError, setUtilsError] = useState(null);

  // Corporate utils (on-demand)
  const [corporateUtils, setCorporateUtils] = useState(() =>
    safeParseSessionJson("corporateUtils")
  );
  const [corporateLoading, setCorporateLoading] = useState(false);
  const [corporateError, setCorporateError] = useState(null);

  // Diet utils (on-demand)
  const [dietCommonUtils, setDietCommonUtils] = useState(() => {
    const raw = safeParseSessionJson("dietCommonUtils");
    if (raw && !isCompleteDietCommonUtils(raw)) {
      try {
        sessionStorage.removeItem("dietCommonUtils");
      } catch {
        /* ignore */
      }
      return null;
    }
    return raw;
  });
  const [dietUtilsLoading, setDietUtilsLoading] = useState(false);
  const [dietUtilsError, setDietUtilsError] = useState(null);

  // Workout common utils (on-demand)
  const [workoutCommonUtils, setWorkoutCommonUtils] = useState(() =>
    safeParseSessionJson("workoutCommonUtils")
  );
  const [workoutUtilsLoading, setWorkoutUtilsLoading] = useState(false);
  const [workoutUtilsError, setWorkoutUtilsError] = useState(null);

  // Fetch session/profile utils on mount
  useEffect(() => {
    async function fetchUtils() {
      setUtilsLoading(true);
      setUtilsError(null);
      try {
        let session = sessionStorage.getItem("sessionUtils");
        let profile = sessionStorage.getItem("profileUtils");
        let usedCache = false;
        if (session && profile) {
          try {
            setSessionUtils(JSON.parse(session));
            setProfileUtils(JSON.parse(profile));
            usedCache = true;
          } catch {
            sessionStorage.removeItem("sessionUtils");
            sessionStorage.removeItem("profileUtils");
          }
        }
        if (!usedCache) {
          const sessionPromise = get(
            "/get-session-utils?company_name=flexcellence&application_name=flexboard",
            { instance: axiosAuthInstance }
          );
          const profilePromise = get(
            "/get-profile-utils",
            { instance: axiosAuthInstance }
          );
          const [sessionData, profileData] = await Promise.all([sessionPromise, profilePromise]);
          setSessionUtils(sessionData);
          setProfileUtils(profileData);
          sessionStorage.setItem("sessionUtils", JSON.stringify(sessionData));
          sessionStorage.setItem("profileUtils", JSON.stringify(profileData));
        }
      } catch (err) {
        setUtilsError("Failed to load session/profile utils");
      }
      setUtilsLoading(false);
    }
    fetchUtils();
  }, []);

  // Corporate utils fetcher (on-demand). `force` skips the sessionStorage copy,
  // for a caller that found a key the cached payload predates.
  const fetchCorporateUtils = async ({ force = false } = {}) => {
    setCorporateLoading(true);
    setCorporateError(null);
    try {
      // Try to use sessionStorage first
      let stored = force ? null : sessionStorage.getItem("corporateUtils");
      if (stored) {
        const parsed = JSON.parse(stored);
        setCorporateUtils(parsed);
        return parsed;
      } else {
        const result = await getCorporateCommonUtils();
        if (result.success && result.data) {
          setCorporateUtils(result.data);
          sessionStorage.setItem("corporateUtils", JSON.stringify(result.data));
          return result.data;
        } else {
          setCorporateError(result.message || "Failed to load corporate utils");
          return null;
        }
      }
    } catch (err) {
      setCorporateError("Failed to load corporate utils");
      return null;
    } finally {
      setCorporateLoading(false);
    }
  };

  // sessionStorage outlives page reloads, so a cached copy can carry meal
  // type ids that were since changed elsewhere (another tab or user) and
  // the API rejects them. Serve the cache, but refetch it once per page load.
  const dietUtilsRevalidatedRef = useRef(false);
  const revalidateDietCommonUtils = async () => {
    if (dietUtilsRevalidatedRef.current) return;
    dietUtilsRevalidatedRef.current = true;
    try {
      const res = await fetchCommonDietUtils();
      if (res.success && isCompleteDietCommonUtils(res.data)) {
        // Unchanged data keeps the current object, so nothing re-renders.
        const json = JSON.stringify(res.data);
        if (json !== sessionStorage.getItem("dietCommonUtils")) {
          setDietCommonUtils(res.data);
          sessionStorage.setItem("dietCommonUtils", json);
        }
      }
    } catch {
      /* keep the cached copy */
    }
  };

  // Diet common utils fetcher (on-demand)
  const fetchDietCommonUtils = async () => {
    let stored = sessionStorage.getItem("dietCommonUtils");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed && isCompleteDietCommonUtils(parsed)) {
          setDietCommonUtils(parsed);
          revalidateDietCommonUtils();
          return parsed;
        }
        sessionStorage.removeItem("dietCommonUtils");
      } catch {
        sessionStorage.removeItem("dietCommonUtils");
      }
    }
    if (dietCommonUtils && isCompleteDietCommonUtils(dietCommonUtils)) {
      revalidateDietCommonUtils();
      return dietCommonUtils;
    }
    setDietUtilsLoading(true);
    setDietUtilsError(null);
    try {
      dietUtilsRevalidatedRef.current = true;
      const res = await fetchCommonDietUtils();
      if (res.success) {
        setDietCommonUtils(res.data);
        sessionStorage.setItem("dietCommonUtils", JSON.stringify(res.data));
        return res.data;
      } else {
        setDietUtilsError(res.message);
      }
    } catch (err) {
      setDietUtilsError("Failed to load diet common utils");
    } finally {
      setDietUtilsLoading(false);
    }
  };

  // Clear diet common utils cache (e.g. when API reports quantity types that no longer exist in DB)
  const clearDietCommonUtilsCache = () => {
    sessionStorage.removeItem("dietCommonUtils");
    setDietCommonUtils(null);
  };

  // Clear workout common utils cache (e.g. after reference-data edits)
  const clearWorkoutCommonUtilsCache = () => {
    sessionStorage.removeItem("workoutCommonUtils");
    setWorkoutCommonUtils(null);
  };

  // Workout common utils fetcher (on-demand)
  const fetchWorkoutCommonUtilsFn = async () => {
    let stored = sessionStorage.getItem("workoutCommonUtils");
    if (stored) {
      setWorkoutCommonUtils(JSON.parse(stored));
      return JSON.parse(stored);
    }
    if (workoutCommonUtils) return workoutCommonUtils;
    setWorkoutUtilsLoading(true);
    setWorkoutUtilsError(null);
    try {
      const res = await fetchWorkoutCommonUtils();
      if (res) {
        setWorkoutCommonUtils(res);
        sessionStorage.setItem("workoutCommonUtils", JSON.stringify(res));
        return res;
      } else {
        setWorkoutUtilsError("Failed to load workout common utils");
      }
    } catch (err) {
      setWorkoutUtilsError("Failed to load workout common utils");
    } finally {
      setWorkoutUtilsLoading(false);
    }
  };

  // A copy restored from sessionStorage at startup is refetched in the background.
  useEffect(() => {
    if (dietCommonUtils) revalidateDietCommonUtils();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  // Fetch workout common utils on mount (workout instance)
  useEffect(() => {
    fetchWorkoutCommonUtilsFn();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);
  // Extract departments_with_roles from corporateUtils
  const departmentsWithRoles = corporateUtils?.departments_with_roles || null;

  return (
    <UtilsContext.Provider
      value={{
        // Data
        sessionUtils,
        profileUtils,
        corporateUtils,
        departmentsWithRoles, // Extracted for easy access
        dietCommonUtils,
        workoutCommonUtils,

        // Loading states
        utilsLoading,         // for session/profile
        corporateLoading,
        dietUtilsLoading,
        workoutUtilsLoading,

        // Error states
        utilsError,           // for session/profile
        corporateError,
        dietUtilsError,
        workoutUtilsError,

        // Fetchers
        fetchCorporateUtils,
        fetchDietCommonUtils,
        clearDietCommonUtilsCache,
        fetchWorkoutCommonUtils: fetchWorkoutCommonUtilsFn,
        clearWorkoutCommonUtilsCache,
      }}
    >
      {children}
    </UtilsContext.Provider>
  );
}

// 3. Custom Hook
export function useUtils() {
  return useContext(UtilsContext);
}
